import { GraphQLClient } from '@refinedev/graphql';
import { initializeApp } from 'firebase/app';
import { Auth, onAuthStateChanged, User } from 'firebase/auth';
import { FirebaseAuth } from '.';
import { getAuthProvider } from './firebaseAuthNew';
import { PORTAL_BUILD_ID } from './portalBuild';
import { appendPortalSignInUsage } from './usageEvents';
import * as fbAuth from 'firebase/auth';
import { isSupported, getAnalytics, Analytics } from 'firebase/analytics';

/**
 * The Firebase project comes from the environment (2026-09-25): the dev server reads the
 * `copingcard-dev` project from `.env.development.local` (`REACT_APP_FIREBASE_*`), and a production
 * build falls back to the prod values below. Until today the portal was hard-coded to PROD, so a local
 * portal signed into prod Firebase against a dev API.
 */
const env = process.env;
export const firebaseConfig = {
  apiKey: env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyAC1ku4BEamHNxUrL8oWkeq-hSATLgxnfE',
  authDomain: env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'copingcard.firebaseapp.com',
  projectId: env.REACT_APP_FIREBASE_PROJECT_ID || 'copingcard',
  storageBucket: env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'copingcard.appspot.com',
  messagingSenderId: env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '783662036013',
  appId: env.REACT_APP_FIREBASE_APP_ID || '1:783662036013:web:093d8dd01ca1e47912b4a4',
  // `??`, not `||`: dev sets it EMPTY on purpose — no analytics into the prod property
  measurementId: env.REACT_APP_FIREBASE_MEASUREMENT_ID ?? 'G-ZXTDZMXEC6',
};

const host = process.env.REACT_APP_CC_API_HOST || '';
const path = process.env.REACT_APP_CC_API_PATH || '';
const API_URL = `${host}${path}`;

// ES coexistence (plan 04 §2.2): the portal is always-latest web — the header tells the API
// `actor.via = portal` for every event a therapist causes; the portal itself has no era, its
// clients do (plan 07 §4).
//
// E30: the middle segment used to be `package.json`.version, which has read `0.0.1` since the
// portal was written — so `es_cohort.lastSeen` recorded "portal, 0.0.1" for every therapist and
// support could not tell which build a person was on. It is now the stamped BUILD ID, the same id
// `/version.json` serves and the freshness check compares against, so the header, the file and the
// bundle all say one thing. Nothing on the server reads this segment as a semver any more: a
// non-mobile client is never judged against the mobile floor (`es/cohort.ts` `clientJudgedByFloor`).
export const httpClient = new GraphQLClient(API_URL, {
  headers: {
    'Authentication': `Bearer `,
    'Accept-Language': 'en',
    'x-cc-client': `portal/${PORTAL_BUILD_ID}/web`,
  },
});

export const firebaseApp = initializeApp(firebaseConfig);

// TODO: they might be not needed, but might help with stale token
const onLogin = async (user: User) => {
  const token = await user.getIdToken();
  httpClient.setHeader('Authentication', `Bearer ${token}`);

  /**
   * E03 (M7) — the portal's `LoggedIn` fan-out, which never existed. Emitted HERE because this is
   * the single point both sign-in paths reach, and because the emit must come AFTER the bearer
   * token is on the client: `createUsageEvents` is authenticated, and an emit one line earlier
   * would post as nobody. Never awaited into the sign-in's critical path and never able to fail it
   * (see `appendPortalSignInUsage`).
   */
  appendPortalSignInUsage().catch(() => undefined);
};

const onLogout = (auth: Auth) => {
  httpClient.setHeader('Authentication', `Bearer `);
};

// export const firebaseAuth = new FirebaseAuth(
//   { onLogin, onLogout },
//   firebaseApp,
// );

export const auth = fbAuth.getAuth(firebaseApp);

export const updateHeaderToken = async () => {
  if (auth.currentUser) {
    const idTokenResult = await fbAuth.getIdTokenResult(auth.currentUser, true);
    httpClient.setHeader('Authentication', `Bearer ${idTokenResult.token}`);
    return idTokenResult?.token;
  }
};

onAuthStateChanged(auth, async (user) => {
  if (user) {
    const token = await user.getIdToken();
    httpClient.setHeader('Authentication', `Bearer ${token}`);
  } else {
    httpClient.setHeader('Authentication', `Bearer `);
  }
});

export const firebaseAuth = getAuthProvider(auth, { onLogin, onLogout });

let analytics = null as Analytics | null;

const maybeInitiateAnalytics = async () => {
  const canIntitiate = await isSupported();
  if (canIntitiate) {
    analytics = getAnalytics(firebaseApp);
  }
};

maybeInitiateAnalytics();

export const maybeGetAnalytics = () => analytics;
